import { breakpoints, colorMap, font, shape, spacing, typography } from './Tokens';
import { createWebTheme } from './functions.web';
import * as lightTokens from './tokens-light.json';
/**
 * 浅色主题的语义 token 修正表（键为语义 token，值为最终颜色，不再解析）。.
 *
 * 底层色板反转为浅色后，部分语义 token 需要单独修正：
 * - text04 / icon04 / labelWhiteIcon：原语义为「亮色底上的深色内容」，
 *   不能随 surface01 反转为白色，否则徽标/通知文字会不可见；
 * - reactionsMenuBackground / gifsBackground：colorMap 内写死了深色字面量，
 *   需在此覆盖为浅色；
 * - conferenceNoticeBackground：顶部提示条在浅色下用白底，避免与页面糊成一片。.
 */
const LIGHT_THEME_SEMANTIC_OVERRIDES = {
    conferenceHeaderBackground: '#FFFFFF',
    conferenceNoticeBackground: '#FFFFFF',
    gifsBackground: '#FFFFFF',
    icon04: '#1F2329',
    labelWhiteIcon: '#1F2329',
    largeVideoBackground: '#F7F8FA',
    notificationBackground: '#FFFFFF',
    reactionsMenuBackground: '#FFFFFF',
    speakingIndicatorBackground: '#E8F2FF',
    speakingIndicatorText: '#1F2329',
    text04: '#1F2329',
    thumbnailLabelBackground: '#FFFFFFCC',
    toolbarActiveScreenShare: '#E6F7ED',
    toolbarBorderColor: '#DEE0E3',
    // 快捷聊天气泡：半透明淡灰（8 位 HEX，末两位为 alpha）
    quickChatPreviewBackground: '#DEE0E3CC',
    quickChatPreviewBackgroundHover: '#DEE0E3EE',
    toolboxBackground: '#F2F3F5',
    toolboxIconHover: '#F0F2F5',
    uiBackground: '#F7F8FA',
    // 小圆头像描边：白边 + 浅灰外环
    circleAvatarBorder: '#FFFFFF',
    circleAvatarRing: 'rgba(31, 35, 41, 0.12)',
    // 「应用」宫格色块（浅色淡底）
    appsGridIconSelectBackground: '#E8F0FE',
    appsGridIconClosedCaptions: '#FFF3E0',
    appsGridIconAudioTranslation: '#E6F4EA',
    appsGridIconWhiteboard: '#F3E5F5'
};
/**
 * 深色主题会议 UI 语义修正（与浅色对称，保持可读对比度）。
 */
const DARK_THEME_SEMANTIC_OVERRIDES = {
    conferenceHeaderBackground: '#1A1D21',
    conferenceNoticeBackground: '#1A1D21',
    gifsBackground: '#1A1D21',
    icon04: '#FFFFFF',
    labelWhiteIcon: '#FFFFFF',
    largeVideoBackground: '#0E0F12',
    notificationBackground: '#1A1D21',
    reactionsMenuBackground: '#1A1D21',
    speakingIndicatorBackground: '#1E3A5F',
    speakingIndicatorText: '#E8F2FF',
    text04: '#FFFFFF',
    thumbnailLabelBackground: '#00000099',
    toolbarActiveScreenShare: '#1A3D2E',
    toolbarBorderColor: '#2E3238',
    // 快捷聊天气泡：半透明深灰（与浅色语义对称）
    quickChatPreviewBackground: '#3D3D3DCC',
    quickChatPreviewBackgroundHover: '#3D3D3DEE',
    toolboxBackground: '#1A1D21',
    toolboxIconHover: '#2E3238',
    uiBackground: '#0E0F12',
    // 小圆头像描边：浅色细边，深底上可见
    circleAvatarBorder: 'rgba(255, 255, 255, 0.88)',
    circleAvatarRing: 'rgba(255, 255, 255, 0.16)',
    // 「应用」宫格色块（深色Muted色块，避免浅色淡底刺眼）
    appsGridIconSelectBackground: '#243656',
    appsGridIconClosedCaptions: '#3D2E1A',
    appsGridIconAudioTranslation: '#1A3D2E',
    appsGridIconWhiteboard: '#3A2438'
};
/** 主题缓存：避免 createTheme 在每次渲染时重复构建。. */
const themeCache = {};
/**
 * 按生效主题获取 MUI theme 对象。.
 *
 * @param {EffectiveThemeMode} mode - 生效主题（light/dark）。.
 * @returns {Theme}
 */
export function getThemeByMode(mode) {
    if (mode === 'light') {
        if (!themeCache.light) {
            themeCache.light = createWebTheme({
                breakpoints,
                colorMap,
                customTokens: lightTokens,
                font,
                semanticOverrides: LIGHT_THEME_SEMANTIC_OVERRIDES,
                shape,
                spacing,
                typography
            });
        }
        return themeCache.light;
    }
    if (!themeCache.dark) {
        themeCache.dark = createWebTheme({
            breakpoints,
            colorMap,
            font,
            semanticOverrides: DARK_THEME_SEMANTIC_OVERRIDES,
            shape,
            spacing,
            typography
        });
    }
    return themeCache.dark;
}
